import type { ReviewCounts, RequiredGap } from "../model/caseReview";
import type { OutstandingTarget } from "./caseReviewOutstanding";
import type { RouteTargetSection } from "./caseReviewRouteTargets";

/** Fills in the screen's name for each missing required field of a count. */
export type GapLabeller = (counts: ReviewCounts) => ReviewCounts;

const gapKey = (fieldId: string, recordId: string | undefined): string => `${fieldId}|${recordId ?? ""}`;

/**
 * A labeller over the route modal's own projection, so a missing field is named here exactly as
 * the route modal names it: the field's catalog title, and the record's title when the field sits
 * on a record — "Result unit — Home pregnancy test", not two indistinguishable "Result unit".
 *
 * <p>Names are looked up only for the gaps a count holds, once each, and cached for the life of
 * the labeller (one render of the page).
 */
export function gapLabeller(project: (targets: OutstandingTarget[]) => RouteTargetSection[]): GapLabeller {
  const names = new Map<string, string>();
  return (counts) => {
    const missing = counts.required.missing;
    if (!missing.length) return counts;
    const unnamed = missing.filter((gap) => !gap.label && !names.has(gapKey(gap.fieldId, gap.recordId)));
    if (unnamed.length) {
      const targets = unnamed.map((gap): OutstandingTarget => gap.recordId === undefined
        ? { field_id: gap.fieldId } : { field_id: gap.fieldId, record_id: gap.recordId });
      for (const section of project(targets)) {
        for (const record of section.records) {
          for (const row of record.rows) {
            names.set(gapKey(row.target.field_id, row.target.record_id),
              record.recordLabel ? `${row.fieldLabel} — ${record.recordLabel}` : row.fieldLabel);
          }
        }
      }
    }
    const labelled = missing.map((gap): RequiredGap => {
      if (gap.label) return gap;
      const label = names.get(gapKey(gap.fieldId, gap.recordId));
      return label ? { ...gap, label } : gap;
    });
    return { ...counts, required: { ...counts.required, missing: labelled } };
  };
}

/**
 * Names a record's missing fields from the record's own schema titles. Used inside a record (its
 * chip, its group headers), where the record is already on screen and needs no naming.
 */
export function labelGapsBySchema(counts: ReviewCounts, schema: { properties?: Record<string, unknown> } | undefined,
                                  keyOf: (fieldId: string) => string | undefined): ReviewCounts {
  const missing = counts.required.missing;
  if (!missing.length) return counts;
  const titleAt = (key: string): string | undefined => {
    let node: { properties?: Record<string, unknown>; title?: string } | undefined = schema;
    for (const segment of key.split(".")) node = node?.properties?.[segment] as typeof node;
    return node?.title;
  };
  const labelled = missing.map((gap): RequiredGap => {
    if (gap.label) return gap;
    const key = keyOf(gap.fieldId);
    const label = key ? titleAt(key) ?? key : undefined;
    return label ? { ...gap, label } : gap;
  });
  return { ...counts, required: { ...counts.required, missing: labelled } };
}
